import type { EligibilityProviderAddress } from "@/src/lib/blocks/eligibility-check-inputs";
import type { NPIResult, NPITaxonomy } from "@/src/lib/npi";
import type { NpiEnumerationType } from "@/src/lib/npi-enumeration";

export type PractitionerRegistryData = NPIResult;

export function isNpiRegistryResult(value: unknown): value is NPIResult {
  return typeof value === "object" && value !== null && "number" in value && "enumeration_type" in value;
}

export function practitionerPrimaryTaxonomy(
  registryData: PractitionerRegistryData | null | undefined
): NPITaxonomy | null {
  if (!isNpiRegistryResult(registryData)) {
    return null;
  }

  return registryData.taxonomies.find((taxonomy) => taxonomy.primary) ?? registryData.taxonomies[0] ?? null;
}

export function practitionerEnumerationType(
  registryData: PractitionerRegistryData | null | undefined
): NpiEnumerationType | null {
  return registryData?.enumeration_type ?? null;
}

export function npiRegistryContactPhoneAndFax(registryData: PractitionerRegistryData | null | undefined): {
  phone: string | null;
  fax: string | null;
} {
  if (!isNpiRegistryResult(registryData)) {
    return { phone: null, fax: null };
  }

  const location =
    registryData.addresses?.find((address) => address.address_purpose === "LOCATION") ?? registryData.addresses?.[0];

  return {
    phone: location?.telephone_number ?? null,
    fax: location?.fax_number ?? null,
  };
}

export function practitionerServiceAddress(
  registryData: PractitionerRegistryData | null | undefined
): EligibilityProviderAddress | null {
  if (!isNpiRegistryResult(registryData)) {
    return null;
  }

  const location =
    registryData.addresses?.find((address) => address.address_purpose === "LOCATION") ?? registryData.addresses?.[0];

  if (!location?.address_1 || !location.city || !location.state) {
    return null;
  }

  const postalCode = location.postal_code?.replace(/\D/g, "").slice(0, 9);

  return {
    address1: location.address_1,
    city: location.city,
    state: location.state,
    postalCode: postalCode && postalCode.length >= 5 ? postalCode : undefined,
  };
}
